// @refresh reload

import * as Sentry from "@sentry/solid"
import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { type Platform, PlatformProvider } from "@/context/platform"
import { dict as en } from "@/i18n/en"
import { dict as zh } from "@/i18n/zh"
import { handleNotificationClick } from "@/utils/notification-click"
import { authFromToken } from "@/utils/server"
import {
  readHostParam,
  readAuthTokenParam,
  readStoredServerUrl,
  writeStoredServerUrl,
  resolveServerUrl,
  stripQueryParams,
} from "@/utils/server-url"
import pkg from "../package.json"
import { ServerConnection } from "./context/server"

const getLocale = () => {
  if (typeof navigator !== "object") return "en" as const
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    if (!language) continue
    if (language.toLowerCase().startsWith("zh")) return "zh" as const
  }
  return "en" as const
}

const getRootNotFoundError = () => {
  const key = "error.dev.rootNotFound" as const
  const locale = getLocale()
  return locale === "zh" ? (zh[key] ?? en[key]) : en[key]
}

const notify: Platform["notify"] = async (title, description, href) => {
  if (!("Notification" in window)) return

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission().catch(() => "denied")
      : Notification.permission

  if (permission !== "granted") return

  const inView = document.visibilityState === "visible" && document.hasFocus()
  if (inView) return

  const notification = new Notification(title, {
    body: description ?? "",
    icon: "https://opencode.ai/favicon-96x96-v3.png",
  })

  notification.onclick = () => {
    handleNotificationClick(href)
    notification.close()
  }
}

const openLink: Platform["openLink"] = (url) => {
  window.open(url, "_blank")
}

const back: Platform["back"] = () => {
  window.history.back()
}

const forward: Platform["forward"] = () => {
  window.history.forward()
}

const restart: Platform["restart"] = async () => {
  window.location.reload()
}

const saveFilePickerDialog: Platform["saveFilePickerDialog"] = async (opts) => {
  const w = window as unknown as { showSaveFilePicker?: (opts: unknown) => Promise<FileSystemFileHandle> }
  if (typeof w.showSaveFilePicker === "function") {
    try {
      const suggestedName = opts?.defaultPath ? opts.defaultPath.split("/").pop() : undefined
      await w.showSaveFilePicker({ suggestedName })
      return opts?.defaultPath ?? "download"
    } catch {
      return null
    }
  }
  return null
}

const exportDebugLogs: Platform["exportDebugLogs"] = async () => {
  const logs: string[] = []
  const now = new Date().toISOString()
  logs.push(`OpenCode Web Debug Logs — ${now}`)
  logs.push(`URL: ${location.href}`)
  logs.push(`User Agent: ${navigator.userAgent}`)
  logs.push("")
  const blob = new Blob([logs.join("\n")], { type: "text/plain" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `opencode-debug-logs-${now.replace(/[:.]/g, "-")}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  return a.download
}

const root = document.getElementById("root")
if (!(root instanceof HTMLElement) && import.meta.env.DEV) {
  throw new Error(getRootNotFoundError())
}

const hostParam = readHostParam(location.search)
const authTokenParam = readAuthTokenParam(location.search)

if (hostParam) writeStoredServerUrl(hostParam)

const serverUrl = resolveServerUrl({
  stored: readStoredServerUrl(),
  hostParam,
  hostname: location.hostname,
  isDev: import.meta.env.DEV,
  devHost: import.meta.env.VITE_OPENCODE_SERVER_HOST,
  devPort: import.meta.env.VITE_OPENCODE_SERVER_PORT,
  protocol: location.protocol,
})

const clearPairingParams = () => {
  const stripped = stripQueryParams(location.search, ["host", "auth_token"])
  if (stripped !== location.search) {
    history.replaceState(null, "", location.pathname + stripped + location.hash)
  }
}

const platform: Platform = {
  platform: "web",
  version: pkg.version,
  openLink,
  back,
  forward,
  restart,
  notify,
  saveFilePickerDialog,
  exportDebugLogs,
  getDefaultServer: async () => {
    const stored = readStoredServerUrl()
    return stored ? ServerConnection.Key.make(stored) : null
  },
  setDefaultServer: writeStoredServerUrl,
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? `web@${pkg.version}`,
    initialScope: {
      tags: {
        platform: "web",
      },
    },
    integrations: (integrations) => {
      return integrations.filter(
        (i) =>
          i.name !== "Breadcrumbs" && !(import.meta.env.OPENCODE_CHANNEL === "prod" && i.name === "GlobalHandlers"),
      )
    },
  })
}

if (root instanceof HTMLElement) {
  const auth = authFromToken(authTokenParam)
  clearPairingParams()
  const server: ServerConnection.Http = {
    type: "http",
    authToken: !!auth,
    http: {
      url: serverUrl,
      ...auth,
    },
  }
  render(
    () => (
      <PlatformProvider value={platform}>
        <AppBaseProviders>
          <AppInterface
            defaultServer={ServerConnection.Key.make(serverUrl)}
            canonicalLocalServer={ServerConnection.key(server)}
            servers={[server]}
            disableHealthCheck
          />
        </AppBaseProviders>
      </PlatformProvider>
    ),
    root,
  )
}
